import React from 'react';
import { 
    Box, 
    Stack, 
    Typography, 
    Paper, 
    Skeleton, 
    useMediaQuery, 
    useTheme 
} from '@mui/material';

const DashboardSkeleton = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box px={isMobile ? 0 : 3} sx={{ minHeight: '100vh' }}>
            {/* Header Skeleton */}
            <Stack 
                direction={isMobile ? 'column' : 'row'} 
                justifyContent="space-between" 
                alignItems={isMobile ? 'flex-start' : 'center'} 
                mb={3} 
                spacing={isMobile ? 2 : 0}
            >
                <Stack direction="row" spacing={4} alignItems="center">
                    <Skeleton variant="rectangular" width={70} height={70} sx={{ borderRadius: 1 }} />
                    <Box>
                        <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width={400} height={20} />
                    </Box>
                </Stack>
            </Stack>

            {/* Stats & Charts Skeleton */}
            <Stack 
                direction={{ xs: 'column', lg: 'row' }} 
                spacing={{ xs: 4, lg: 4 }} 
                mb={4}
            >
                <Box 
                    flex={{ lg: 2 }} 
                    display="flex" 
                    flexDirection="column" 
                    gap={4}
                >
                    {/* Bar Chart Skeleton */}
                    <Box sx={{ minHeight: { lg: '480px' } }}>
                        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px" }}>
                            <Skeleton variant="text" width={150} height={32} sx={{ mb: 3 }} />
                            
                            {/* Stats Cards Skeleton */}
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                mb={4}
                                sx={{
                                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                    '& > *': {
                                        flex: { xs: '1 1 100%', sm: '1 1 0' },
                                        minWidth: 'auto'
                                    }
                                }}
                            >
                                {[1, 2, 3, 4].map((i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            px: 2,
                                            py: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: '#FFF',
                                            border: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box>
                                            <Skeleton variant="text" width={80} height={16} sx={{ mb: 0.5 }} />
                                            <Skeleton variant="text" width={60} height={24} />
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            {/* Chart Skeleton */}
                            <Box sx={{ 
                                width: '100%', 
                                height: { xs: 250, sm: 275, md: 300 },
                                mt: { xs: 2, sm: 3 }
                            }}>
                                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
                            </Box>
                        </Paper>
                    </Box>

                    {/* Recently Added Skeleton */}
                    <Box>
                        <Paper elevation={3} sx={{ p: 3, borderRadius: "12px" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Skeleton variant="text" width={150} height={32} />
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Skeleton variant="text" width={80} height={20} />
                                    <Skeleton variant="circular" width={16} height={16} />
                                </Stack>
                            </Box>
                            <Stack spacing={1} sx={{ maxHeight: "300px" }}>
                                {[1, 2, 3, 4].map((i) => (
                                    <Paper
                                        key={i}
                                        variant="outlined"
                                        sx={{ p: 1, borderRadius: "28px", backgroundColor: "#FAF9F9" }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 60 }}>
                                            <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 3, flexGrow: 1 }}>
                                                {[1, 2, 3, 4].map((j) => (
                                                    <Stack key={j} spacing={0.5}>
                                                        <Skeleton variant="text" width={60} height={16} />
                                                        <Skeleton variant="text" width={80} height={20} />
                                                    </Stack>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Paper>
                    </Box>
                </Box>

                <Box 
                    flex={{ lg: 1 }} 
                    display="flex" 
                    flexDirection="column" 
                    gap={6}
                >
                    {/* Donut Chart Skeleton */}
                    <Box>
                        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "12px", height: '100%' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Skeleton variant="text" width={100} height={32} />
                                <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                                <Skeleton variant="circular" width={200} height={200} sx={{ mb: 3 }} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, width: '100%' }}>
                                    {[1, 2, 3, 4].map((i) => (
                                        <Box key={i} display="flex" alignItems="center" gap={2}>
                                            <Skeleton variant="circular" width={8} height={8} />
                                            <Skeleton variant="text" width={60} height={20} />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    {/* Users Skeleton */}
                    <Box sx={{ mt: { xs: 2, lg: 1.5 } }}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: "12px" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Skeleton variant="text" width={80} height={32} />
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Skeleton variant="text" width={60} height={20} />
                                    <Skeleton variant="circular" width={16} height={16} />
                                </Stack>
                            </Box>
                            <Stack spacing={1} sx={{ maxHeight: 400 }}>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <Stack key={i} direction="row" alignItems="center" spacing={1}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box flex={1}>
                                            <Skeleton variant="text" width={100} height={20} />
                                            <Skeleton variant="text" width={80} height={16} />
                                        </Box>
                                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};

export default DashboardSkeleton;
